import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtQty, todayStr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Lock, LockOpen } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { ShipStatus, d10 } from "@/components/common/cells";
import { FormField } from "@/components/stock/FormField";
import { selectCls } from "@/components/stock/LineItemsEditor";
import { ShipmentLinesEditor, emptyLine, KINDS } from "@/components/shipments/ShipmentLinesEditor";
import { toast } from "sonner";

const STATUSES = ["planned", "booked", "shipped", "cancelled"];
const LOCKED = ["shipped", "cancelled"];
const DATE_FIELDS = ["stuffing_date", "po_date", "etd", "eta"];
const FORM_FIELDS = ["plan_code", "stuffing_date", "buyer_po", "po_date", "destination_country", "forwarder",
  "shipping_line", "vessel", "bl_number", "booking_number", "etd", "eta"];
const LINE_KEYS = ["line_id", "product_id", "packing_style_id", ...KINDS.map((k) => k.qty)];

const RefSel = ({ value, onChange, options, label, testid }) => (
  <FormField label={label}>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={selectCls} data-testid={testid}>
      <option value="">—</option>
      {options.map((o) => <option key={o.id} value={o.id}>{o.code} · {o.name}</option>)}
    </select>
  </FormField>
);

const cleanLines = (lines) => lines.map((ln) => Object.fromEntries(LINE_KEYS.map((k) => [k, k.endsWith("_qty") ? Number(ln[k] || 0) : (ln[k] || null)])));

const ItemsCell = ({ r, kind }) => (
  <div className="space-y-0.5">
    {(r.lines_detail || []).map((d, i) => (d[kind].qty > 0 ? (
      <div key={i} className={`font-mono text-xs whitespace-nowrap ${d[kind].shortage > 1e-9 ? "text-red-700 font-semibold" : ""}`}>
        {d[kind].code} · {fmtQty(d[kind].qty)}
      </div>
    ) : <div key={i} className="text-xs text-slate-400">-</div>))}
  </div>
);

export default function Shipments() {
  const { t } = useLang();
  const { user, can } = useAuth();
  const canEdit = can("shipments", "edit");
  const isAdmin = user?.role === "admin";
  const [refs, setRefs] = useState({ maps: [], buyers: [], products: [], brands: [], packs: [], fgs: [], cartons: [], plastics: [] });
  const [shipments, setShipments] = useState([]);
  const [form, setForm] = useState(null);
  const [alloc, setAlloc] = useState([]);

  const loadAll = async () => {
    const keys = ["maps", "buyers", "products", "brands", "packs", "fgs", "cartons", "plastics"];
    const urls = ["/mappings", "/masters/buyers", "/masters/products", "/masters/brands", "/masters/packing-styles", "/masters/finish-goods", "/masters/cartons", "/masters/plastics"];
    const [sh, ...res] = await Promise.all([api.get("/shipments"), ...urls.map((u) => api.get(u))]);
    setShipments(sh.data);
    setRefs(Object.fromEntries(keys.map((k, i) => [k, res[i].data])));
  };
  useEffect(() => { loadAll(); }, []);

  const linesKey = form ? JSON.stringify([form.lines, form.stuffing_date, form.etd, form.status, form.buyer_id, form.brand_id]) : "";
  useEffect(() => {
    if (!form) { setAlloc([]); return; }
    const h = setTimeout(() => {
      api.post("/calc/shipment-shortage", { id: form.id, plan_code: form.plan_code, buyer_id: form.buyer_id || null, brand_id: form.brand_id || null, stuffing_date: form.stuffing_date || null, etd: form.etd || null, status: form.status, lines: cleanLines(form.lines) })
        .then((r) => setAlloc(r.data.lines)).catch(() => setAlloc([]));
    }, 350);
    return () => clearTimeout(h);
  }, [linesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => setForm({
    plan_code: `SHP-${new Date().getFullYear()}-${Math.floor(Math.random() * 9000 + 1000)}`,
    buyer_id: "", brand_id: "", status: "planned", stuffing_date: "", po_date: todayStr(), etd: "", eta: "", lines: [emptyLine()],
  });
  const openEdit = (s) => setForm({ ...s, lines: (s.lines || []).map((l) => ({ ...l })), ...Object.fromEntries(DATE_FIELDS.map((k) => [k, (s[k] || "").slice(0, 10)])) });

  const save = async () => {
    const body = Object.fromEntries(FORM_FIELDS.map((k) => [k, form[k] || null]));
    Object.assign(body, { buyer_id: form.buyer_id, brand_id: form.brand_id || null, status: form.status, remark: form.remark || null, lines: cleanLines(form.lines) });
    try {
      if (form.id) await api.put(`/shipments/${form.id}`, body);
      else await api.post("/shipments", body);
      toast.success(t("saved"));
      setForm(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const unlock = async (s) => {
    if (!window.confirm(s.status === "shipped" ? t("unlock_confirm_shipped") : t("unlock_confirm"))) return;
    try { await api.post(`/shipments/${s.id}/unlock`); toast.success(t("unlocked")); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/shipments/${id}`); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const columns = useMemo(() => {
    const txt = (key, label, cls = "") => ({ key, label: t(label || key), className: `whitespace-nowrap ${cls}` });
    const date = (key) => ({ key, label: t(key), className: "font-mono text-xs whitespace-nowrap", value: (r) => d10(r[key]) });
    const items = (kind, label) => ({ key: `${kind}_code`, label: t(label), render: (r) => <ItemsCell r={r} kind={kind} /> });
    const list = (key, label) => ({ key, label: t(label), render: (r) => (
      <div className="space-y-0.5">{(r.lines_detail || []).map((d, i) => <div key={i} className="text-xs whitespace-nowrap">{d[key]}</div>)}</div>
    ) });
    return [
      txt("plan_code", "plan_code", "font-mono text-xs"), date("stuffing_date"), txt("buyer", "buyers"), txt("buyer_po", "buyer_po", "font-mono text-xs"),
      date("po_date"), txt("brand", "brands"), txt("destination_country"), list("product", "products"),
      { key: "remark", label: t("remark"), className: "text-xs text-slate-600 min-w-[140px]" },
      list("packing_style", "packing_styles"),
      items("fg", "finish_goods"), items("carton", "cartons"), items("plastic", "plastics"),
      txt("forwarder"), txt("shipping_line"), txt("vessel"), txt("bl_number", "bl_number", "font-mono text-xs"), txt("booking_number", "booking_number", "font-mono text-xs"),
      date("etd"), date("eta"),
      { key: "status", label: t("status"), align: "center", render: (r) => <ShipStatus status={r.status} /> },
      { key: "actions", label: "", filter: false, sort: false, render: (r) => (LOCKED.includes(r.status) || canEdit) && (
        <div className="flex justify-end whitespace-nowrap">
          {LOCKED.includes(r.status)
            ? (isAdmin
              ? <Button variant="ghost" size="sm" onClick={() => unlock(r)} className="text-amber-700" title={t("unlock")} data-testid={`shipment-unlock-${r.plan_code}`}><LockOpen size={14} /></Button>
              : <span title={t("shipment_locked")} className="px-3 text-slate-400" data-testid={`shipment-locked-${r.plan_code}`}><Lock size={14} /></span>)
            : <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`shipment-edit-${r.plan_code}`}><Pencil size={14} /></Button>}
          {canEdit && r.status !== "shipped" && <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="text-red-600" data-testid={`shipment-delete-${r.plan_code}`}><Trash2 size={14} /></Button>}
        </div>
      ) },
    ];
  }, [t, canEdit, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (v) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-6" data-testid="shipments-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("shipments")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("shipment_subtitle")}</p>
        </div>
        {canEdit && (
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="create-shipment-btn">
            <Plus size={14} className="mr-2" />{t("create_shipment")}
          </Button>
        )}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("shipments")} ({shipments.length})</h3>
          </div>
          <DataTable columns={columns} rows={shipments} testid="shipments-table" />
        </CardContent>
      </Card>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="shipment-form-dialog">
          <DialogHeader><DialogTitle>{form?.id ? t("edit") : t("add")} - {t("shipments")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <RefSel value={form.buyer_id} onChange={set("buyer_id")} options={refs.buyers} label={t("buyers")} testid="shipment-buyer-select" />
                <RefSel value={form.brand_id} onChange={set("brand_id")} options={refs.brands} label={t("brands")} testid="shipment-brand-select" />
                {FORM_FIELDS.map((k) => (
                  <FormField key={k} label={t(k)}>
                    <Input type={DATE_FIELDS.includes(k) ? "date" : "text"} value={form[k] ?? ""}
                           onChange={(e) => set(k)(e.target.value)} data-testid={`shipment-${k}-input`} />
                  </FormField>
                ))}
                <FormField label={t("status")}>
                  <select className={selectCls} value={form.status} onChange={(e) => set("status")(e.target.value)} data-testid="shipment-status-select">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </FormField>
                <FormField label={t("remark")} className="col-span-2 md:col-span-3">
                  <Input value={form.remark || ""} onChange={(e) => set("remark")(e.target.value)} data-testid="shipment-remark-input" />
                </FormField>
              </div>
              <ShipmentLinesEditor lines={form.lines} setLines={set("lines")} refs={refs} buyerId={form.buyer_id} brandId={form.brand_id} alloc={alloc} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>{t("cancel")}</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="shipment-save-btn">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
